/**
 * MoodMate — mood catalogue.
 *
 * Pure data + helpers, no React. Everything the feature shows about a mood (its
 * colour, the greeting line, the sample suggestions and which interactive
 * activity it opens) is declared here, so the UI stays a thin renderer and a new
 * mood is one entry rather than edits across six components.
 *
 * Nothing here diagnoses or labels the person. A mood is a self-reported check-in,
 * the copy is supportive and optional, and no suggestion claims a live external
 * integration — they are sample prompts shown during development.
 */

export type MoodId = 'happy' | 'calm' | 'focused' | 'sad' | 'anxious' | 'tired';

/** The little playful experience a mood opens. All are optional and easy to leave. */
export type ActivityType =
  | 'gratitude' | 'breathing' | 'focus' | 'journal' | 'grounding' | 'rest' | 'visual'
  | 'bubbles' | 'garden' | 'waves' | 'stars' | 'pond' | 'release' | 'zen'
  // Mini-game activities (the "wellness playground" set).
  | 'burst' | 'odd' | 'wins' | 'explore' | 'power';

export interface MoodSuggestion {
  icon: string;
  title: string;
  desc: string;
}

export interface MoodDef {
  id: MoodId;
  label: string;
  /** One short line under the mood label on the selection card. */
  tagline: string;
  /** A friendly glyph — recognisable at a glance, no image asset required. */
  emoji: string;
  /** Mood accent + a soft tint for backgrounds. Scoped to MoodMate; brand teal still frames the page. */
  accent: string;
  soft: string;
  /** The second line of the dashboard greeting, phrased as an invitation, never a verdict. */
  greeting: string;
  affirmation: string;
  /**
   * Playful, kind one-liners shown as the reaction when this mood is picked. One
   * is chosen at random. Humour is warm and workplace-appropriate; for the lower
   * moods (sad, anxious, tired) it is gentle and supportive, never making light of
   * how the person feels.
   */
  reactions: string[];
  /** "Recommended for you" — sample content, clearly suggestions, no external links. */
  suggestions: MoodSuggestion[];
  /** The one interactive activity surfaced in "Wellness mode". */
  activity: { type: ActivityType; title: string; desc: string };
}

export const MOODS: MoodDef[] = [
  {
    id: 'happy',
    label: 'Happy',
    tagline: 'Bright and upbeat',
    emoji: '😊',
    accent: '#f4b740',
    soft: '#fff6e2',
    greeting: 'You’re feeling happy today. Let’s make the most of that good energy.',
    affirmation: 'Savour this — you’ve earned a good moment.',
    reactions: [
      'Your happiness is currently running on premium mode!',
      'Warning: excessive smiling detected. Keep spreading the good vibes!',
      'Today, even your coffee is trying to keep up with you!',
    ],
    suggestions: [
      { icon: '🎵', title: 'Uplifting playlist', desc: 'Queue up something that matches your energy (suggestion).' },
      { icon: '✍️', title: 'Gratitude note', desc: 'Jot down three things going well right now.' },
      { icon: '☀️', title: 'Share the lift', desc: 'A quick kind message to a teammate.' },
    ],
    activity: { type: 'burst', title: 'Happy Burst', desc: 'Tap the floating smileys before they pop away.' },
  },
  {
    id: 'calm',
    label: 'Calm',
    tagline: 'Peaceful and settled',
    emoji: '🌿',
    accent: '#3fb9a0',
    soft: '#e4f6f1',
    greeting: 'You’re feeling calm today. Let’s make space for a peaceful day.',
    affirmation: 'Steady and unhurried — a good place to be.',
    reactions: [
      'Zen mode activated. Even your notifications are afraid to disturb you.',
      'You’re so calm, even the loading spinner has stopped panicking.',
      'Inner peace: 100%. Inbox peace: still loading.',
    ],
    suggestions: [
      { icon: '🫧', title: 'Box breathing', desc: 'A couple of slow, even breaths to stay centred.' },
      { icon: '🎧', title: 'Ambient sounds', desc: 'Soft background audio while you work (suggestion).' },
      { icon: '🧘', title: 'Mindful minute', desc: 'One minute of simply noticing your breath.' },
    ],
    activity: { type: 'breathing', title: 'Breathing Bubble', desc: 'Follow the bubble as it expands and shrinks — a few slow breaths.' },
  },
  {
    id: 'focused',
    label: 'Focused',
    tagline: 'Sharp and ready',
    emoji: '🎯',
    accent: '#4f86f0',
    soft: '#e9effd',
    greeting: 'You’re feeling focused today. Let’s protect that momentum.',
    affirmation: 'In the zone — one task at a time.',
    reactions: [
      'Focus mode activated. Distractions have left the meeting.',
      'You’re locked in. Even your tabs are behaving today.',
      'Mission mode: ON. Random scrolling: politely declined.',
    ],
    suggestions: [
      { icon: '⏱️', title: 'Focus timer', desc: 'A single, distraction-free work block.' },
      { icon: '📋', title: 'Pick one task', desc: 'Choose the one thing that matters most right now.' },
      { icon: '🔕', title: 'Quiet notifications', desc: 'Mute non-urgent pings for a while (your call).' },
    ],
    activity: { type: 'odd', title: 'Find the Odd One', desc: 'Spot the one symbol that’s different — a quick focus warm-up.' },
  },
  {
    id: 'sad',
    label: 'Sad',
    tagline: 'Low and tender',
    emoji: '💙',
    accent: '#6f8fba',
    soft: '#eef2f9',
    greeting: 'You’re feeling low today. Be gentle with yourself — small steps are enough.',
    affirmation: 'It’s okay to feel this. You don’t have to fix it all at once.',
    reactions: [
      'Even cloudy skies take coffee breaks. Take yours too.',
      'Not every day has to be sunshine. You’re still doing better than you think.',
      'Today’s forecast: cloudy, with a chance of things getting better.',
    ],
    suggestions: [
      { icon: '📓', title: 'A few words', desc: 'Write down whatever’s on your mind, just for you.' },
      { icon: '🎵', title: 'Gentle music', desc: 'Something soft and unhurried (suggestion).' },
      { icon: '🌱', title: 'One small thing', desc: 'Pick a tiny, manageable task — that counts.' },
    ],
    activity: { type: 'wins', title: 'Tiny Wins', desc: 'Pick one small thing you managed today — it counts.' },
  },
  {
    id: 'anxious',
    label: 'Anxious',
    tagline: 'Keyed-up or uneasy',
    emoji: '🪷',
    accent: '#a98bd6',
    soft: '#f3ecfb',
    greeting: 'You’re feeling anxious today. Let’s slow things down together, one breath at a time.',
    affirmation: 'You’re safe in this moment. Let’s take it slowly.',
    reactions: [
      'Brain has opened 47 tabs. Let’s gently close a few.',
      'One breath at a time. Your brain doesn’t need to solve the entire universe today.',
      'Your worry department is working overtime. Let’s schedule a tiny break.',
    ],
    suggestions: [
      { icon: '🫧', title: 'Guided breathing', desc: 'Lengthen the exhale to settle your system.' },
      { icon: '🖐️', title: 'Grounding (5-4-3-2-1)', desc: 'Come back to your senses, one at a time.' },
      { icon: '⏸️', title: 'Permission to pause', desc: 'Step away for a minute — it’s allowed.' },
    ],
    activity: { type: 'explore', title: '5-4-3-2-1 Explorer', desc: 'A gentle grounding walk through your senses, one step at a time.' },
  },
  {
    id: 'tired',
    label: 'Tired',
    tagline: 'Low on energy',
    emoji: '🌙',
    accent: '#8a94a6',
    soft: '#eef0f4',
    greeting: 'You’re feeling tired today. Let’s keep it light and build in some rest.',
    affirmation: 'Rest is productive too. Go easy.',
    reactions: [
      'Battery level: 12%. Power-saving mode recommended.',
      'Your brain has requested a software update and a nap.',
      'Running on coffee and determination? A tiny break might help.',
    ],
    suggestions: [
      { icon: '☕', title: 'Short break', desc: 'A proper few minutes away from the screen.' },
      { icon: '🧏', title: 'Gentle stretch', desc: 'Roll your shoulders, loosen your neck.' },
      { icon: '🌙', title: 'Wind down', desc: 'When the day allows, an earlier night helps.' },
    ],
    activity: { type: 'power', title: 'Power-Up Meter', desc: 'Tap to fill your battery with a little cheerful energy.' },
  },
];

export const getMood = (id?: MoodId | null): MoodDef | undefined =>
  id ? MOODS.find((m) => m.id === id) : undefined;

/**
 * A random playful reaction line for a mood. `avoid` lets the caller skip the
 * line just shown, so tapping the same mood twice doesn't repeat the quote.
 */
export const pickReaction = (id: MoodId, avoid?: string): string => {
  const lines = getMood(id)?.reactions ?? [];
  if (lines.length === 0) return '';
  const pool = avoid && lines.length > 1 ? lines.filter((l) => l !== avoid) : lines;
  return pool[Math.floor(Math.random() * pool.length)];
};

/** The time-of-day half of the greeting, e.g. "Good morning". */
export const greetingPrefix = (now: Date = new Date()): string => {
  const h = now.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};
